import { useState, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { InAppNotification } from '../../../types';
import { pickLocalized } from '../../../utils/localized';
import { SubmitButton } from '../Buttons/SubmitButton/SubmitButton';
import {
  useNotificationActionHandler,
  useNotificationLanguage,
  resolveActionLabel,
  actionEmoji,
  parseMediaUrls,
} from './useActionHandler';
import styles from './Simple.module.css';

interface SimpleProps {
  notification: InAppNotification;
  onDismiss: () => void;
}

type MediaItem = { kind: 'image' | 'video'; url: string };

const Simple = ({ notification, onDismiss }: SimpleProps) => {
  const { t } = useTranslation('common');
  const [imageError, setImageError] = useState<Record<number, boolean>>({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const handleAction = useNotificationActionHandler(onDismiss);
  const lang = useNotificationLanguage();
  const titleHtml = pickLocalized(notification.title, lang);
  const descriptionHtml = pickLocalized(notification.description, lang);

  // Smooth swipe handling
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartX = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const minSwipeDistance = 50;

  // Комбинируем изображения и видео в единый слайдер (изображения, затем видео)
  const mediaItems = useMemo<MediaItem[]>(() => {
    const images = parseMediaUrls(notification.imageUrl, notification.imageUrls);
    const videos = parseMediaUrls(notification.videoUrl, notification.videoUrls);
    return [
      ...images.map<MediaItem>((url) => ({ kind: 'image', url })),
      ...videos.map<MediaItem>((url) => ({ kind: 'video', url })),
    ];
  }, [notification.imageUrl, notification.imageUrls, notification.videoUrl, notification.videoUrls]);

  const imageUrls = mediaItems; // сохраняем имя для минимальных изменений ниже
  const hasMultipleImages = imageUrls.length > 1;
  const isCurrentImageError = imageError[currentImageIndex];
  
  // Smooth swipe handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!hasMultipleImages) return;
    
    // Предотвращаем системные жесты (pull-to-refresh, edge swipe)
    e.stopPropagation();
    
    touchStartX.current = e.targetTouches[0].clientX;
    setIsDragging(true);
    setDragOffset(0);
  }, [hasMultipleImages]);
  
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || !hasMultipleImages) return;
    
    // Предотвращаем системные жесты
    e.preventDefault();
    e.stopPropagation();
    
    const currentX = e.targetTouches[0].clientX;
    const diff = currentX - touchStartX.current;
    
    // Ограничиваем свайп на краях (эластичный эффект)
    const isAtStart = currentImageIndex === 0 && diff > 0;
    const isAtEnd = currentImageIndex === imageUrls.length - 1 && diff < 0;
    
    if (isAtStart || isAtEnd) {
      setDragOffset(diff * 0.3); // Эластичный эффект на краях
    } else {
      setDragOffset(diff);
    }
  }, [isDragging, hasMultipleImages, currentImageIndex, imageUrls.length]);
  
  const handleTouchEnd = useCallback(() => {
    if (!isDragging || !hasMultipleImages) return;
    
    setIsDragging(false);
    
    const containerWidth = containerRef.current?.offsetWidth || 300;
    const threshold = Math.min(minSwipeDistance, containerWidth * 0.2);
    
    if (dragOffset < -threshold && currentImageIndex < imageUrls.length - 1) {
      // Свайп влево - следующее
      setCurrentImageIndex(prev => prev + 1);
    } else if (dragOffset > threshold && currentImageIndex > 0) {
      // Свайп вправо - предыдущее
      setCurrentImageIndex(prev => prev - 1);
    }
    
    setDragOffset(0);
  }, [isDragging, hasMultipleImages, dragOffset, currentImageIndex, imageUrls.length]);
  
  // Расчёт смещения для карусели
  const getSliderTransform = useCallback(() => {
    const containerWidth = containerRef.current?.offsetWidth || 300;
    const baseOffset = -currentImageIndex * containerWidth;
    return baseOffset + dragOffset;
  }, [currentImageIndex, dragOffset]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onDismiss();
    }
  };

  return createPortal(
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.popup}>
        {/* Image slider (if images exist) */}
        {imageUrls.length > 0 && !isCurrentImageError && (
          <div 
            ref={containerRef}
            className={styles.imageContainer}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
          >
            <div 
              className={styles.sliderTrack}
              style={{
                transform: `translateX(${getSliderTransform()}px)`,
                transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              }}
            >
              {imageUrls.map((item, index) => (
                <div key={index} className={styles.slideItem}>
                  {item.kind === 'video' ? (
                    <video
                      src={item.url}
                      className={styles.video}
                      controls
                      controlsList="nodownload"
                      playsInline
                      preload={index <= currentImageIndex + 1 ? 'metadata' : 'none'}
                      onError={() => setImageError(prev => ({ ...prev, [index]: true }))}
                      onClick={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      onTouchMove={(e) => e.stopPropagation()}
                      onTouchEnd={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <img
                      src={item.url}
                      alt={`${titleHtml} ${index + 1}`}
                      className={styles.image}
                      onError={() => setImageError(prev => ({ ...prev, [index]: true }))}
                      loading={index <= currentImageIndex + 1 ? 'eager' : 'lazy'}
                      draggable={false}
                    />
                  )}
                </div>
              ))}
            </div>
            {hasMultipleImages && (
              <>
                <button
                  className={`${styles.sliderArrow} ${styles.sliderArrowLeft}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (currentImageIndex > 0) {
                      setCurrentImageIndex(prev => prev - 1);
                    }
                  }}
                  style={{ opacity: currentImageIndex === 0 ? 0.3 : 1 }}
                >
                  ‹
                </button>
                <button
                  className={`${styles.sliderArrow} ${styles.sliderArrowRight}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (currentImageIndex < imageUrls.length - 1) {
                      setCurrentImageIndex(prev => prev + 1);
                    }
                  }}
                  style={{ opacity: currentImageIndex === imageUrls.length - 1 ? 0.3 : 1 }}
                >
                  ›
                </button>
                <div className={styles.sliderDots}>
                  {imageUrls.map((_, index) => (
                    <button
                      key={index}
                      className={`${styles.sliderDot} ${index === currentImageIndex ? styles.active : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex(index);
                      }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Content */}
        <div className={styles.content}>
          <h2 
            className={styles.title}
            dangerouslySetInnerHTML={{ __html: titleHtml }}
          />
          <div
            className={styles.description}
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        </div>

        {/* Action buttons */}
        {notification.actions && notification.actions.length > 0 && (
          <div className={styles.actionsContainer}>
            {notification.actions.map((action, index) => (
              <button
                key={index}
                className={styles.actionButton}
                onClick={() => handleAction(action)}
              >
                {actionEmoji(action.type)}
                {resolveActionLabel(action, lang)}
              </button>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className={styles.footer}>
          <SubmitButton label={t('btn.ok')} onClick={onDismiss} />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Simple;

